import init, { Session, UUID, UserCommandResponseVec } from "keyserver-client";
import { Chain } from "./Chains";
import { Storage } from "./Storage";
import { buildAndEncrypt, BuilderMetadata } from "./Metadata";
import { generateRootKey } from "./Crypto";
import "core-js/proposals/array-buffer-base64";

// TODO: Some of the code isn't fully chain-agnostish, yet. For example any
// place that uses this hard-coded collection.
export const SEPOLIA_PUBLIC_COLLECTION =
  "0x5251378f7036536325b9e2dab6d93c5924108d67";

export const SEPOLIA_PUBLIC_COLLECTION_LOCALID =
  "0x0000000000000000000000005251378f7036536325b9e2dab6d93c5924108d67";

export enum Step {
  KeyReservation,
  Uploading,
  Minting,
  SettingPermissions,
  AwaitingFinality,
  Done,
  MetaMaskRejected = 100,
}
// Maybe we should add and display an estimated duration
export interface ProgressReport {
  step: Step;
  key_id?: string;
  tokenId?: BigInt;
  estimate?: Date; // Estimation when this step will be completed
}

// Returns the tokenID as a hex-string (0x-prefixed).
export async function encryptAndMint(
  metadata: BuilderMetadata,
  storage: Storage,
  chain: Chain,
  onProgress: (report: ProgressReport) => void,
) {
  // Initialize WASM. Doing this here means it only happens when needed.
  // Alternatively we could do this in the background after the page is loaded.
  await init();

  // Key used for encryption (stored in the keyserver)
  const tempToken = "INSECURE"; // TODO: Generate randomly
  const [key, key_bytes] = await generateRootKey();

  // 1. Reserve an entry on the keyserver (needed because of a circular dependency between tokenID, metadata_url and key_id)
  // TODO: Session creation (connecting) could probably be done outside of this function, too (thus allowing re-use),
  // but keep in mind that the keyserver can terminate sessions at will (especially long-running ones).
  const keyserverSession = await Session.connect(KEYSERVER_URL);
  // TODO: Using key_raw.words is probably wrong, as we need [u8] and not [u32].
  const key_id = await reserveKeyId(keyserverSession, key_bytes, tempToken);
  console.log(key_id);

  // 2. Create (partially encrypted) metadata json
  const encrypted_metadata = await buildAndEncrypt(metadata, key_id, key);

  // 3. Upload file(s) (at the moment we're using data urls and thus only have to upload the metadata json)
  // For now use the keyserver (if we go with ipfs we may want to do it directly instead of going through
  // the keyserver).
  const report: ProgressReport = {
    step: Step.Uploading,
  };
  onProgress(report);
  const metadata_url = await storage.upload(encrypted_metadata);
  console.log(metadata_url);

  // 4. Mint on-chain
  report.step = Step.Minting;
  onProgress(report);
  let tokenid;
  try {
    tokenid = await chain.mint(metadata_url);
  } catch (e) {
    console.error(e);
    report.step = Step.MetaMaskRejected;
    onProgress(report);
    return null;
  }
  console.log(tokenid);

  // Store key in localStorage to allow viewing the NFT even when the mint isn't
  // finalized, yet.
  // TODO: Make this chain+collection aware.
  const localStorageKey = localStorageKeyName(
    SEPOLIA_PUBLIC_COLLECTION,
    tokenid,
  );
  localStorage.setItem(localStorageKey, key_bytes.toBase64());

  // 5. "Publish" Key
  // TODO: At the moment we wait until the transaction is minted, we do not wait on confirmation.
  // This works, but the keyserver has to wait a while and in it could happen that a reordering
  // happens within the Ethereum epoch (unlikely but possible). Therefore we can either wait
  // for one block (as done now) and accept the risk that our on-chain tokenid may have changed,
  // but we cannot update the permissions anymore because we already did so, or we wait longer
  // before setting the permissions (in the second case we should probably store tempToken
  // somewhere in local storage in case the browser window is closed).
  report.tokenId = tokenid;
  report.step = Step.SettingPermissions;
  onProgress(report);
  const res = await setKeyPermissions(
    keyserverSession,
    key_id,
    tokenid,
    tempToken,
  );
  console.log(res);

  const est = chain.waitFinalized();
  report.step = Step.AwaitingFinality;
  report.estimate = est.estimate;
  onProgress(report);
  await est.promise;
  report.step = Step.Done;
  onProgress(report);
}

// export const KEYSERVER_DOMAIN = "localhost:4000";
// export const KEYSERVER_DOMAIN = "13.80.3.215:9401";
export const KEYSERVER_DOMAIN = "keyserver.erdstall.dev";
export const KEYSERVER_URL = `https://${KEYSERVER_DOMAIN}`;

export function localStorageKeyName(
  collection: string,
  tokenId: BigInt,
): string {
  return `cnft-decryption-key-sepolia/${collection}/${tokenId}`;
}

interface PublicData {
  metadata?: object;
  owner: string;
}

export async function fetchPublic(
  collection: string,
  tokenID: BigInt,
  chain: Chain,
): Promise<PublicData> {
  const metadata_url = await chain.readMetadataUrl(collection, tokenID);
  console.log(metadata_url);
  return {
    metadata: await fetchMetadata(metadata_url),
    owner: await chain.ownerOf(collection, tokenID),
  };
}

// TODO: Check if the mint was finalized and don't bother asking the Keyserver
// if it hasn't yet (return error with time estimate).
export async function fetchKey(
  tokenID: BigInt,
  chain: Chain,
  key_id: string,
): Promise<Uint32Array> {
  // Initialize WASM. Doing this here means it only happens when needed.
  // Alternatively we could do this in the background after the page is loaded.
  await init();

  const keyserverSession = await Session.connect(KEYSERVER_URL);
  const eip4361 = await chain.auth();
  const key = await getKeyWithNftAuth(
    keyserverSession,
    key_id,
    tokenID,
    eip4361,
  );
  // TODO: Make this chain+collection aware.
  const localStorageKey = localStorageKeyName(
    SEPOLIA_PUBLIC_COLLECTION,
    tokenID,
  );
  localStorage.setItem(localStorageKey, new Uint8Array(key).toBase64());
  return key;
}

export const FETCH_FALLBACK_DELAY_MS = 5000;

// This function is used for all tokens and should support all storage backends.
// It is not included in `Storage` because we may not know where something is stored
// in advance (e.g. if that is stored in the url schema).
async function fetchMetadata(url: string): Promise<object> {
  return await fetchStaggeredFallback(
    toBrowserUrls(url),
    FETCH_FALLBACK_DELAY_MS,
    (res) => res.json(),
  );
}

// 404, timeouts and other error codes are treated as a failed request.
export async function fetchStaggeredFallback<T>(
  urls: string[],
  delay_ms: number,
  then: (res: Response) => Promise<T>,
): Promise<T> {
  const controller = new AbortController();
  const res = await Promise.any(
    urls.map(async (url, index) => {
      if (index > 0) {
        await new Promise((resolve) => setTimeout(resolve, index * delay_ms));
      }
      const res = await fetch(url, { signal: controller.signal });
      if (!res.ok) {
        throw Error(res.statusText);
      } else {
        // As far as I can tell we have to await the body before aborting the
        // other requests. We could do this outside of the map but that'd fail
        // if any url returns the header but is slow in returning the body. In
        // practice it shouldn't really matter.
        return await then(res);
      }
    }),
  );
  controller.abort();
  return res as T;
}

export function toBrowserUrls(url: string): string[] {
  if (url.startsWith("ipfs://")) {
    return ipfsCidToBrowserUrls(url.slice(7));
  } else if (/^[A-Za-z0-9]+$/.test(url)) {
    // For some reason OpenSea treats urls without a schema as ipfs (at least if
    // their format matches).
    return ipfsCidToBrowserUrls(url);
  } else {
    return [url];
  }
}

function ipfsCidToBrowserUrls(cid: string): string[] {
  return [
    `https://ipfs.io/ipfs/${cid}`,
    `https://gateway.pinata.cloud/ipfs/${cid}`,
  ];
}

const PERM_NONE = 0x00;
// const PERM_READ_DATA = 0x01;
const PERM_WRITE_ACCESS_CONTROL = 0x08;
const PERM_READ = 0x5;

function toU32Array(buf: Uint8Array): number[] {
  // I don't know of a better way to get the data into the (u32) number[]
  // wasm-bindgen expects.
  const bytes: number[] = [];
  new Uint32Array(buf).forEach((word) => {
    bytes.push(word);
  });
  return bytes;
}

// We do this to ensure that we have control over the UUID before minting it. If
// the Keyserver would just accept our UUID we still couldn't be sure it is ours,
// only probabilistically.
async function reserveKeyId(
  session: Session,
  key: Uint8Array,
  tempToken: string,
): Promise<UUID> {
  const command = {
    Store: {
      confidential: {
        data: {
          // We may want a variant for "raw" key/bytes intended for PBKDF (if we
          // add support in the enclave for that).
          Unstructured: toU32Array(key),
        },
        checks: [{ Secret: tempToken }], // We could also use HashPreimage here (requires calculating the hash of our own temporary token)
      },
      public: {
        checks: [],
        base_permissions: PERM_NONE,
        rules: [
          {
            permissions: PERM_WRITE_ACCESS_CONTROL,
            required_checks: [0x01], // bitmask of checks
            threshold: 0,
            threshold_checks: [],
          },
        ],
        id: "00000000-0000-0000-0000-000000000000", // Set by the TEE
      },
    },
  };
  const res = await session.send_usercommands([command], undefined, [], []);

  // TODO: Proper error handling (and no clue how to do a proper match on this)
  const res0 = res[0] as any;
  if ("Created" in res0) {
    return res0.Created!;
  } else {
    throw "Bad response";
  }
  reserveKeyId;
}

async function setKeyPermissions(
  session: Session,
  id: UUID,
  tokenid: BigInt,
  tempToken: string,
): Promise<UserCommandResponseVec> {
  const command = {
    Update: {
      id, // Tell the TEE which entry we intend to modify
      witness: [
        {
          id: 0, // We only have one check
          value: {
            Secret: tempToken,
          },
        },
      ],
      update: {
        data: undefined, // Unchanged; TODO: Can we make tsify to allow/expect null for this?
        public_checks: [
          {
            NftOwner: {
              origin: 1, //TODO: Don't hard-code this
              local_id: SEPOLIA_PUBLIC_COLLECTION_LOCALID,
              token: "0x" + tokenid.toString(16).padStart(64, "0"),
            },
          },
        ],
        confidential_checks: [], // Removes our tempToken
        base_permissions: PERM_NONE, // could be null, having this here is more explicit and easier to read
        rules: [
          {
            // TODO: At the moment the Keyserver doesn't distinguish between
            // data and access control (even though the permissions do). For
            // us it doesn't really matter, since we don't use confidential
            // access control, but strictly speaking this should be READ_DATA.
            permissions: PERM_READ,
            required_checks: [0x01], // bitmask of checks
            threshold: 0,
            threshold_checks: [],
          },
        ],
      },
    },
  };
  return session.send_usercommands([command], id, [], []);
}

async function getKeyWithNftAuth(
  session: Session,
  id: UUID,
  tokenID: BigInt,
  eip4361: { message: string; sig: string },
): Promise<Uint32Array> {
  const commands = [
    {
      AuthEIP4361: eip4361,
    },
    {
      Get: { id, witness: [] },
    },
  ];
  console.log("Commands:", commands);
  const nft_witness_request = {
    check: 0,
    chain_id: 1, //TODO: Don't hard-code this
    contract: SEPOLIA_PUBLIC_COLLECTION_LOCALID,
    token: "0x" + tokenID.toString(16).padStart(64, "0"),
  };
  console.log("Witness Request:", nft_witness_request);
  const res = await session.send_usercommands(
    commands,
    id,
    [nft_witness_request],
    [],
  );
  console.log("Keyserver response:", res);

  // TODO: Proper error handling (and no clue how to do a proper match on this)
  const res0 = res[1] as any;
  if ("Get" in res0) {
    return new Uint32Array(res0.Get.confidential.data.Unstructured);
  } else {
    throw "Bad response";
  }
}

export function toDataURL(file: Blob | File): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.readAsDataURL(file);
  });
}
